import Dinero from 'dinero.js'

import { CustomerRelationship } from 'utils/enums'
import { QuoteAttributes } from 'components/funeral-quote-form-fields'
import { Contact } from 'lib/models/contact'
import { Crematorium } from 'lib/models/crematorium'

export const disputesQuestions = [
  {
    name: 'quoteAttributes.hasObjectionsToFuneral',
    label:
      'Has the caller confirmed they’re the decision maker and the family is in agreement?',
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
]

type FormikBoolean = 'true' | 'false' | ''

export type LeadQuoteAttributes = QuoteAttributes & {
  alreadyDeceased: FormikBoolean
  ashesSplitting: FormikBoolean
  ashesSplittingPortions?: number | null
  attendedService?: FormikBoolean
  customerIsArrangingTheirOwnFuneral: FormikBoolean
  deceasedReferredToCoroner: FormikBoolean
  customerRefersToThemAs: string
  crematoriumId?: number | null
  crematorium?: Crematorium
  dateOfBirth: string
  dateOfDeath: string
  title: string
  firstName: string
  lastName: string
  treeType?: string
  treePlaqueMessage?: string
  urnType?: string
  hasHazardousImplant: string | null
  hasObjectionsToFuneral: FormikBoolean
  relationshipToCustomer: CustomerRelationship | ''
}

export type FormikValues = {
  calculatedPrice: number
  contact?: Contact
  quoteAttributes: LeadQuoteAttributes
  sourceIdentifier: string
  quotedPrices: {
    price: number
  }
  voucherCode?: string
}

type DisputeOutcome = {
  complete: boolean
  heading: string
  message: string
}

const DISPUTE_MESSAGES = {
  CANNOT_PROCEED: {
    complete: true,
    heading: 'Disputed',
    message:
      "We can't take on a case unless everyone in the family is in agreement",
  },
  CAN_PROCEED: {
    complete: true,
    heading: 'No disputes',
    message: 'Proceed to next section',
  },
}

export const hasAgreementToProceed = (
  values: FormikValues
): true | false | null => {
  const { hasObjectionsToFuneral } = values.quoteAttributes

  // Due to a bug, hasObjectionsToFuneral is 'true' when we can proceed and 'false' when we can't.
  // This is the opposite to what's expected.
  if (hasObjectionsToFuneral === 'true') {
    return true
  } else if (hasObjectionsToFuneral === 'false') {
    return false
  }

  return null
}

export const outcomeForDisputes = ({
  values,
}: {
  values: FormikValues
}): DisputeOutcome | Record<string, never> => {
  if (hasAgreementToProceed(values) === true) {
    return DISPUTE_MESSAGES.CAN_PROCEED
  } else if (hasAgreementToProceed(values) === false) {
    return DISPUTE_MESSAGES.CANNOT_PROCEED
  }

  return {}
}

export const skipPayment = ({ values }: { values: FormikValues }): boolean => {
  if (hasAgreementToProceed(values) === false) {
    return true
  } else if (
    !quoteIsComplete({ values }) ||
    hasAgreementToProceed(values) == null
  ) {
    return true
  }

  return false
}

export const quoteIsComplete = ({
  values,
}: {
  values: FormikValues
}): boolean => {
  const {
    deceasedReferredToCoroner,
    deceasedInMortuary,
    deathCountryId,
    packageSlug,
  } = values.quoteAttributes

  return (
    (deceasedInMortuary === 'false' ||
      (deceasedInMortuary === 'true' && !!deceasedReferredToCoroner)) &&
    !!deathCountryId &&
    !!packageSlug
  )
}

export const formatPrice = (price: number): string => {
  return `+${Dinero({
    amount: price,
  }).toFormat('$0,0')}`
}

export const formatOptionalPrice = (price?: number): string => {
  if (!price) return '-'

  return formatPrice(price)
}

export const formatDistance = (distanceKm: number): string => {
  const distanceInMiles = Math.round(distanceKm / 1.609)

  if (distanceInMiles < 1) {
    return '< 1 mile'
  }

  if (distanceInMiles === 1) {
    return '1 mile'
  }

  return `${distanceInMiles} miles`
}
